import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const infoItems = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    label: '所在地',
    value: '〒000-0000 東京都○○区○○町1-2-3 さくらビル1F',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6.29 6.29l.98-.98a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    label: '電話番号',
    value: '0120-000-000（受付時間：平日 8:30〜17:30）',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    label: '営業時間',
    value: '月曜〜土曜 8:30〜17:30（日曜・祝日・年末年始を除く）',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    label: '運営法人',
    value: '社会福祉法人 さくら福祉会',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    label: '定員',
    value: '1日30名（要介護1〜5、要支援1〜2の方が対象）',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    label: '指定番号',
    value: '指定通所介護事業所番号：1234567890',
  },
];

export default function AccessSection() {
  const { ref, inView } = useInView();

  return (
    <section id="access" className="py-24 md:py-32 bg-[#f8faf6]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-px w-8 bg-[#88B277]" />
            <span className="text-[#88B277] text-sm font-semibold tracking-widest uppercase">Access &amp; Info</span>
            <div className="h-px w-8 bg-[#88B277]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
            基本情報・<span className="text-[#88B277]">アクセス</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Info Table */}
          <div
            className={`transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {infoItems.map((item, i) => (
                <div
                  key={item.label}
                  className={`flex items-start gap-4 p-5 ${
                    i < infoItems.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-[#88B277]/10 rounded-xl text-[#88B277] shrink-0 mt-0.5">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-1">{item.label}</p>
                    <p className="text-gray-700 text-sm leading-relaxed">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Phone */}
            <div className="mt-6 p-6 bg-[#88B277] rounded-2xl text-center">
              <p className="text-white/80 text-sm mb-2">まずはお気軽にご相談ください</p>
              <a
                href="tel:0120-000-000"
                className="text-white font-bold text-2xl tracking-wider hover:opacity-90 transition-opacity cursor-pointer"
              >
                0120-000-000
              </a>
              <p className="text-white/70 text-xs mt-2">受付時間：平日 8:30〜17:30</p>
            </div>
          </div>

          {/* Map */}
          <div
            className={`transition-all duration-700 delay-400 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="rounded-2xl overflow-hidden border border-gray-100 h-full min-h-80">
              <iframe
                title="さくらデイサービス 地図"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3240.827853357766!2d139.74454931525882!3d35.68949888019432!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188bfbd89f700b%3A0x277c49ba34ed38!2z5p2x5Lqs6aeF!5e0!3m2!1sja!2sjp!4v1620000000000!5m2!1sja!2sjp"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
